import { MouseEvent } from "react";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import {
  PURCHASE_ORDER_CHAIN_LABEL,
  PurchaseOrderListItem,
} from "../../types/purchase-order.type";
import { formatNumber } from "../../utils";
import CustomerMatchPanel from "./CustomerMatchPanel";
import PurchaseOrderStatusChip from "./StatusChip";
import {
  branchRefText,
  customerRefText,
  formatCalendarDate,
  isReturnable,
  PO_EMPTY_TEXT,
  PurchaseOrderMatching,
  returnByText,
} from "./data";

interface PurchaseOrderRowProps {
  row: PurchaseOrderListItem;
  /** แถบสลับสีของแถวคู่/คี่ ให้สายตาไล่ตามแถวยาว ๆ ได้ */
  striped: boolean;
  /** จำนวนคอลัมน์ทั้งตาราง สำหรับแถวแผงจับคู่ร้านที่กินเต็มความกว้าง */
  columnCount: number;
  onOpenDetail: (uuid: string) => void;
  /** ติ๊กเลือกใบ (W1 และ W7) */
  selected?: boolean;
  onToggleSelect?: (uuid: string) => void;
  canSelect?: boolean;
  /** tooltip ของช่องติ๊กที่กดไม่ได้ เช่น "ใช้แล้ว" */
  disabledReason?: string | null;
  /** W7 เท่านั้น: ปุ่ม ส่งคืน HO ของแถวนี้ */
  onReturn?: (row: PurchaseOrderListItem) => void;
  /** W7 เท่านั้น: แผงจับคู่ร้านใต้แถวที่ยังไม่มีร้าน */
  matching?: PurchaseOrderMatching;
}

/** หนึ่งแถวของรายการ PO และ (เฉพาะ W7) แผงจับคู่ร้านที่ต่อท้ายแถวนั้น */
export default function PurchaseOrderRow({
  row,
  striped,
  columnCount,
  onOpenDetail,
  selected,
  onToggleSelect,
  canSelect = false,
  disabledReason = null,
  matching,
  onReturn,
}: PurchaseOrderRowProps) {
  const matchedShopText = customerRefText(row.customer);
  const shopText = matchedShopText || row.ship_to_name || PO_EMPTY_TEXT;
  const showReturn = Boolean(onReturn) && isReturnable(row);
  const showMatchPanel = Boolean(matching) && row.status === "UNMATCHED_CUSTOMER";

  const handleOpenDetail = () => {
    onOpenDetail(row.uuid);
  };

  // ช่องติ๊กอยู่ในแถวที่กดแล้วเปิดรายละเอียด การติ๊กจึงต้องไม่เปิด drawer ตามไปด้วย
  const handleSelectCellClick = (event: MouseEvent<HTMLTableCellElement>) => {
    event.stopPropagation();
  };

  const handleToggleSelect = () => {
    if (onToggleSelect) onToggleSelect(row.uuid);
  };

  // ปุ่มอยู่ในแถวที่กดแล้วเปิดรายละเอียด จึงต้องไม่เปิด drawer ตามไปด้วย
  const handleReturn = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (onReturn) onReturn(row);
  };

  const checkbox = (
    <Checkbox
      size="small"
      checked={Boolean(selected)}
      disabled={!canSelect}
      onChange={handleToggleSelect}
      inputProps={{ "aria-label": `เลือก PO ${row.po_number}` }}
    />
  );

  return (
    <>
      <TableRow
        onClick={handleOpenDetail}
        sx={{
          cursor: "pointer",
          bgcolor: (theme) =>
            striped
              ? theme.palette.background.default
              : theme.palette.background.paper,
        }}
      >
        {onToggleSelect && (
          <TableCell padding="checkbox" onClick={handleSelectCellClick}>
            {/* ช่องที่กดไม่ได้บอกเหตุผลผ่าน tooltip · span รับ hover แทนปุ่มที่ disabled */}
            {!canSelect && disabledReason ? (
              <Tooltip title={disabledReason}>
                <span>{checkbox}</span>
              </Tooltip>
            ) : (
              checkbox
            )}
          </TableCell>
        )}

        <TableCell>
          <Typography variant="body2" fontWeight={600}>
            {row.po_number}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {PURCHASE_ORDER_CHAIN_LABEL[row.chain]}
          </Typography>
        </TableCell>

        <TableCell>{formatCalendarDate(row.delivery_date)}</TableCell>

        <TableCell>
          {row.branch ? (
            branchRefText(row.branch)
          ) : (
            <Typography variant="body2" color="secondary.main" fontWeight={600}>
              {branchRefText(row.branch)}
            </Typography>
          )}
        </TableCell>

        <TableCell>{row.route_number || PO_EMPTY_TEXT}</TableCell>

        <TableCell>
          <Typography variant="body2">{shopText}</Typography>
          <Typography variant="caption" color="text.secondary">
            รหัสในไฟล์ {row.ship_to_code}
          </Typography>
        </TableCell>

        <TableCell align="right">{formatNumber(row.amount)}</TableCell>

        <TableCell align="center">
          <Stack alignItems="center" gap={0.5}>
            <PurchaseOrderStatusChip status={row.status} />
            {row.return_info && (
              <Stack sx={{ maxWidth: 220 }}>
                <Typography
                  variant="caption"
                  color="warning.dark"
                  fontWeight={600}
                  sx={{ lineHeight: 1.3 }}
                >
                  {row.return_info.reason}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ lineHeight: 1.3 }}
                >
                  {returnByText(row.return_info)}
                </Typography>
              </Stack>
            )}
            {showReturn && (
              <Button size="small" color="warning" onClick={handleReturn}>
                ส่งคืน HO
              </Button>
            )}
          </Stack>
        </TableCell>
      </TableRow>

      {matching && showMatchPanel && (
        <TableRow
          sx={{ bgcolor: (theme) => theme.palette.background.default }}
        >
          <TableCell colSpan={columnCount} sx={{ py: 1.5 }}>
            <CustomerMatchPanel
              purchaseOrder={row}
              refreshToken={matching.refreshToken}
              onMatched={matching.onMatched}
              onOpenNewCustomer={matching.onOpenNewCustomer}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
